<template>
  <AnalyticsWidget
    title="Order Production Summary"
    subTitle="Production summary data for orders completed during each date range"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight"
    >
    <LoadingPanel :loading="loading" />
    <AnalyticsBreakdownTable v-if="!loading">
      <template v-slot:header>
        <tr>
          <th></th>
          <th>Order Count</th>
          <th>Avg. Stages Per Order</th>
          <th>Avg. Production Duration Per Order</th>
          <th>Avg. Stage Production Duration Per Order</th>
          <th>Avg. Shifts To Complete Production Per Order</th>
        </tr>
      </template>
      <template v-slot:body>
        <tr class="highlight">
          <td>
            Today
            <InlineHelpPopover title="Today" placement="right" inline>
              <p>Today&apos;s completed orders compared to previous working day&apos;s orders on {{formatDate(orderProductionBreakdown.day.previous.startDate)}}</p>              
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.day.current.totalOrderCount" :previous="orderProductionBreakdown.day.previous.totalOrderCount" highlight>{{formatInteger(orderProductionBreakdown.day.current.totalOrderCount)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.day.current.avgStageCountPerOrder" :previous="orderProductionBreakdown.day.previous.avgStageCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.day.current.avgStageCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.day.current.avgProductionDurationPerOrder" :previous="orderProductionBreakdown.day.previous.avgProductionDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.day.current.avgProductionDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.day.current.avgStageDurationPerOrder" :previous="orderProductionBreakdown.day.previous.avgStageDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.day.current.avgStageDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.day.current.averageShiftRequiredCountPerOrder" :previous="orderProductionBreakdown.day.previous.averageShiftRequiredCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.day.current.averageShiftRequiredCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Week
            <InlineHelpPopover title="This Week" placement="right" inline>
              <p>This week&apos;s completed orders compared to same day of the previous week&apos;s orders from {{formatDate(orderProductionBreakdown.week.previous.startDate)}} to {{formatDate(orderProductionBreakdown.week.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.week.current.totalOrderCount" :previous="orderProductionBreakdown.week.previous.totalOrderCount" highlight>{{formatInteger(orderProductionBreakdown.week.current.totalOrderCount)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.week.current.avgStageCountPerOrder" :previous="orderProductionBreakdown.week.previous.avgStageCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.week.current.avgStageCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.week.current.avgProductionDurationPerOrder" :previous="orderProductionBreakdown.week.previous.avgProductionDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.week.current.avgProductionDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.week.current.avgStageDurationPerOrder" :previous="orderProductionBreakdown.week.previous.avgStageDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.week.current.avgStageDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.week.current.averageShiftRequiredCountPerOrder" :previous="orderProductionBreakdown.week.previous.averageShiftRequiredCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.week.current.averageShiftRequiredCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This Month
            <InlineHelpPopover title="This Month" placement="right" inline>
              <p>This month&apos;s completed orders compared to same day of the previous month&apos;s orders from {{formatDate(orderProductionBreakdown.month.previous.startDate)}} to {{formatDate(orderProductionBreakdown.month.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.month.current.totalOrderCount" :previous="orderProductionBreakdown.month.previous.totalOrderCount" highlight>{{formatInteger(orderProductionBreakdown.month.current.totalOrderCount)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.month.current.avgStageCountPerOrder" :previous="orderProductionBreakdown.month.previous.avgStageCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.month.current.avgStageCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.month.current.avgProductionDurationPerOrder" :previous="orderProductionBreakdown.month.previous.avgProductionDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.month.current.avgProductionDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.month.current.avgStageDurationPerOrder" :previous="orderProductionBreakdown.month.previous.avgStageDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.month.current.avgStageDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.month.current.averageShiftRequiredCountPerOrder" :previous="orderProductionBreakdown.month.previous.averageShiftRequiredCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.month.current.averageShiftRequiredCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
        </tr>
        <tr>
          <td>
            This FY
            <InlineHelpPopover title="This FY" placement="right" inline>
              <p>This financial year&apos;s completed orders compared to same day of the previous financial year&apos;s orders from {{formatDate(orderProductionBreakdown.financialYear.previous.startDate)}} to {{formatDate(orderProductionBreakdown.financialYear.previous.endDate)}}</p>
            </InlineHelpPopover>
          </td>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.financialYear.current.totalOrderCount" :previous="orderProductionBreakdown.financialYear.previous.totalOrderCount" highlight>{{formatInteger(orderProductionBreakdown.financialYear.current.totalOrderCount)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.financialYear.current.avgStageCountPerOrder" :previous="orderProductionBreakdown.financialYear.previous.avgStageCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.financialYear.current.avgStageCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.financialYear.current.avgProductionDurationPerOrder" :previous="orderProductionBreakdown.financialYear.previous.avgProductionDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.financialYear.current.avgProductionDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.financialYear.current.avgStageDurationPerOrder" :previous="orderProductionBreakdown.financialYear.previous.avgStageDurationPerOrder" highlight>{{displayBreakdownDuration(orderProductionBreakdown.financialYear.current.avgStageDurationPerOrder)}}</AnalyticsBreakdownValueCell>
          <AnalyticsBreakdownValueCell :value="orderProductionBreakdown.financialYear.current.averageShiftRequiredCountPerOrder" :previous="orderProductionBreakdown.financialYear.previous.averageShiftRequiredCountPerOrder" highlight>{{formatDecimal(orderProductionBreakdown.financialYear.current.averageShiftRequiredCountPerOrder, 2, true)}}</AnalyticsBreakdownValueCell>
        </tr>
      </template>
    </AnalyticsBreakdownTable>
  </AnalyticsWidget>
</template>


<script>
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsBreakdownTable from '@/modules/intelligence/components/analyticsBreakdownTable'
import AnalyticsBreakdownValueCell from '@/modules/intelligence/components/analyticsBreakdownValueCell'

export default {
  name: 'OrderProductionBreakdownWidget',
  components: {
    AnalyticsWidget,
    AnalyticsBreakdownTable,
    AnalyticsBreakdownValueCell
  },
  props: {
    gridWidth: Number,
    gridHeight: Number
  },
  computed: {
    currentMonthName() {
      return moment().local().format('MMMM')
    }
  },
  data() {
    return {
      loading: true,
      orderProductionBreakdown: {},
    }
  },
  created() {
  },
  mounted() {
    this.loading = true
    this.requestData()
  },
  methods: {
    requestData() {
      this.$apiService.analyticsService.getOrderProductionBreakdown()
      .then(response => {
        this.orderProductionBreakdown = response.data
      })
      .catch(err => {
        console.error(err)
      })
      .finally(() => {
        this.loading = false
      })
    },
    displayBreakdownDuration(timeMinutes) {
      if(timeMinutes < 60)
        return `${this.formatDecimal(timeMinutes, 1, true)} mins`

      return `${this.formatDecimal(timeMinutes / 60, 2, true)} hrs`
    }
  }
}
</script>